import { z } from 'zod';

import {
  PrimaryKeySchema,
  commaDelimitedList,
  withSnakeCaseSupport,
} from './utils';

// Define a base step schema with common timestamp fields
const BaseStepSchema = z.object({
  startedAt: z.coerce.date().optional(),
  endedAt: z.coerce.date().optional(),
  summary: z.string().optional(),
});

// Define the specific step schemas using camelCase
const TaskStepSchema = BaseStepSchema.extend({
  stepType: z.literal('task'),
  task: z.string(),
});

const ThoughtStepSchema = BaseStepSchema.extend({
  stepType: z.literal('thought'),
  thought: z.string(),
});

const CodeStepSchema = BaseStepSchema.extend({
  stepType: z.literal('code'),
  code: z.string(),
});

const ObservationStepSchema = BaseStepSchema.extend({
  stepType: z.literal('observation'),
  observation: z.string(),
});

const PlanStepSchema = BaseStepSchema.extend({
  stepType: z.literal('plan'),
  plan: z.string(),
});

const FactStepSchema = BaseStepSchema.extend({
  stepType: z.literal('fact'),
  facts: z.string(),
});

const SystemStepSchema = BaseStepSchema.extend({
  stepType: z.literal('system'),
  system: z.string(),
});

const FinalAnswerStepSchema = BaseStepSchema.extend({
  stepType: z.literal('final_answer'),
  finalAnswer: z.object({
    type: z.literal('final_answer'),
    output: z.string(),
    resourceIds: z.array(z.string()).optional(),
  }),
});

// Create union with snake_case support for each step type
export const AgentExecutionMemoryStateItemSchema = z.union([
  withSnakeCaseSupport(TaskStepSchema),
  withSnakeCaseSupport(ThoughtStepSchema),
  withSnakeCaseSupport(CodeStepSchema),
  withSnakeCaseSupport(ObservationStepSchema),
  withSnakeCaseSupport(PlanStepSchema),
  withSnakeCaseSupport(FactStepSchema),
  withSnakeCaseSupport(SystemStepSchema),
  withSnakeCaseSupport(FinalAnswerStepSchema),
]);

/**
 * Schema for agent execution memory states which store versioned memory data
 */
export const AgentExecutionMemoryStateSchema = z.object({
  agentExecutionMemoryStateId: PrimaryKeySchema,
  agentExecutionId: PrimaryKeySchema,
  state: z.record(z.any()),
  version: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AgentExecutionMemoryState = z.infer<
  typeof AgentExecutionMemoryStateSchema
>;

export const AgentExecutionMemoryStatesSchema = z.array(
  AgentExecutionMemoryStateSchema,
);

export type AgentExecutionMemoryStates = z.infer<
  typeof AgentExecutionMemoryStatesSchema
>;

export const CreateAgentExecutionMemoryStateSchema =
  AgentExecutionMemoryStateSchema.pick({
    agentExecutionId: true,
    state: true,
    version: true,
  }).partial({
    version: true, // version is optional, will be auto-incremented if not provided
  });

export type CreateAgentExecutionMemoryState = z.infer<
  typeof CreateAgentExecutionMemoryStateSchema
>;

export const AgentExecutionMemoryStatesQueryOptionsSchema = z.object({
  agentExecutionMemoryStateIds: commaDelimitedList(
    z.string().array(),
  ).optional(),
  agentExecutionIds: commaDelimitedList(z.string().array()).optional(),
  versions: commaDelimitedList(z.number().array()).optional(),
  latest: z.boolean().optional(), // when combined with agentExecutionId, gets the latest version
});

export type AgentExecutionMemoryStatesQueryOptions = z.infer<
  typeof AgentExecutionMemoryStatesQueryOptionsSchema
>;
